import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { Separator } from '../ui/separator';
import { Palette, Monitor, Sun, Moon, Wallpaper, Type } from 'lucide-react';
import { useTheme } from 'next-themes';

export const AppearanceSettings: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const [wallpaper, setWallpaper] = useState('default');
  const [fontSize, setFontSize] = useState([14]);
  const [iconSize, setIconSize] = useState([32]);
  const [windowTransparency, setWindowTransparency] = useState([85]);
  const [animations, setAnimations] = useState(true);
  const [fontFamily, setFontFamily] = useState('system');
  const [accentColor, setAccentColor] = useState('blue');
  const [showTaskbarLabels, setShowTaskbarLabels] = useState(true);
  const [autoHideTaskbar, setAutoHideTaskbar] = useState(false);

  const wallpapers = [
    { id: 'default', name: 'Default Maple Art', preview: '🏔️' },
    { id: 'space', name: 'Space Theme', preview: '🌌' },
    { id: 'forest', name: 'Digital Forest', preview: '🌲' },
    { id: 'city', name: 'Maple City', preview: '🏙️' },
    { id: 'abstract', name: 'Abstract Patterns', preview: '🎨' },
    { id: 'retro', name: 'Retro Waves', preview: '🌊' },
  ];

  const accentColors = [
    { id: 'blue', name: 'Blue', color: 'bg-blue-500' },
    { id: 'green', name: 'Green', color: 'bg-green-500' },
    { id: 'purple', name: 'Purple', color: 'bg-purple-500' },
    { id: 'red', name: 'Red', color: 'bg-red-500' },
    { id: 'orange', name: 'Orange', color: 'bg-orange-500' },
    { id: 'pink', name: 'Pink', color: 'bg-pink-500' },
  ];

  return (
    <div className="h-full p-6 bg-background overflow-auto">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-2">Appearance Settings</h1>
          <p className="text-muted-foreground">
            Customize the look and feel of your desktop
          </p>
        </div>

        {/* Theme */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {theme === 'dark' ? <Moon size={20} /> : <Sun size={20} />}
              Theme
            </CardTitle>
            <CardDescription>
              Choose between light and dark mode
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <Button
                variant={theme === 'light' ? 'default' : 'outline'}
                onClick={() => setTheme('light')}
                className="h-20 flex flex-col items-center justify-center gap-2"
              >
                <Sun size={24} />
                <span>Light</span>
              </Button>
              <Button
                variant={theme === 'dark' ? 'default' : 'outline'}
                onClick={() => setTheme('dark')}
                className="h-20 flex flex-col items-center justify-center gap-2"
              >
                <Moon size={24} />
                <span>Dark</span>
              </Button>
              <Button
                variant={theme === 'system' ? 'default' : 'outline'}
                onClick={() => setTheme('system')}
                className="h-20 flex flex-col items-center justify-center gap-2"
              >
                <Monitor size={24} />
                <span>System</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Wallpaper */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallpaper size={20} />
              Wallpaper
            </CardTitle>
            <CardDescription>
              Select your desktop background
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {wallpapers.map((wp) => (
                <Button
                  key={wp.id}
                  variant={wallpaper === wp.id ? 'default' : 'outline'}
                  onClick={() => setWallpaper(wp.id)}
                  className="h-24 flex flex-col items-center justify-center gap-2"
                >
                  <div className="text-2xl">{wp.preview}</div>
                  <span className="text-xs">{wp.name}</span>
                </Button>
              ))}
            </div>

            <Separator />

            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                Browse Files
              </Button>
              <Button variant="outline" size="sm">
                Take Screenshot
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Colors & Accent */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette size={20} />
              Colors
            </CardTitle>
            <CardDescription>
              Choose accent colors for the interface
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Accent Color</Label>
              <div className="grid grid-cols-6 gap-2">
                {accentColors.map((color) => (
                  <Button
                    key={color.id}
                    variant={accentColor === color.id ? 'default' : 'outline'}
                    onClick={() => setAccentColor(color.id)}
                    className="h-12 flex items-center justify-center"
                  >
                    <div className={`w-6 h-6 rounded-full ${color.color}`}></div>
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Typography */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Type size={20} />
              Typography
            </CardTitle>
            <CardDescription>
              Configure fonts and text sizing
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="font-family">Font Family</Label>
              <Select value={fontFamily} onValueChange={setFontFamily}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="system">System Default</SelectItem>
                  <SelectItem value="mono">Monospace</SelectItem>
                  <SelectItem value="serif">Serif</SelectItem>
                  <SelectItem value="pixel">Maple Font</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="font-size">Font Size</Label>
                <span className="text-sm text-muted-foreground">
                  {fontSize[0]}px
                </span>
              </div>
              <Slider
                id="font-size"
                value={fontSize}
                onValueChange={setFontSize}
                min={10}
                max={24}
                step={1}
                className="w-full"
              />
            </div>

            <div className="p-4 bg-muted/30 rounded-lg">
              <div className="text-sm">Font Preview:</div>
              <div style={{ fontSize: `${fontSize[0]}px` }} className="mt-2">
                The quick brown fox jumps over the lazy dog. 1234567890
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Desktop & Icons */}
        <Card>
          <CardHeader>
            <CardTitle>Desktop & Icons</CardTitle>
            <CardDescription>
              Configure desktop layout and icon appearance
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="icon-size">Icon Size</Label>
                <span className="text-sm text-muted-foreground">
                  {iconSize[0]}px
                </span>
              </div>
              <Slider
                id="icon-size"
                value={iconSize}
                onValueChange={setIconSize}
                min={16}
                max={64}
                step={4}
                className="w-full"
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <Label htmlFor="show-taskbar-labels">Show taskbar labels</Label>
              <Switch
                id="show-taskbar-labels"
                checked={showTaskbarLabels}
                onCheckedChange={setShowTaskbarLabels}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="auto-hide-taskbar">Auto-hide taskbar</Label>
              <Switch
                id="auto-hide-taskbar"
                checked={autoHideTaskbar}
                onCheckedChange={setAutoHideTaskbar}
              />
            </div>
          </CardContent>
        </Card>

        {/* Visual Effects */}
        <Card>
          <CardHeader>
            <CardTitle>Visual Effects</CardTitle>
            <CardDescription>
              Control animations and transparency
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <Label htmlFor="animations">Enable animations</Label>
              <Switch
                id="animations"
                checked={animations}
                onCheckedChange={setAnimations}
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="window-transparency">Window Transparency</Label>
                <span className="text-sm text-muted-foreground">
                  {windowTransparency[0]}%
                </span>
              </div>
              <Slider
                id="window-transparency"
                value={windowTransparency}
                onValueChange={setWindowTransparency}
                min={0}
                max={100}
                step={5}
                className="w-full"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline">Reset to Defaults</Button>
          <Button>Apply Changes</Button>
        </div>
      </div>
    </div>
  );
};